import { t } from '@lingui/macro';
import { AccountModel, BankDto, PaymentAccountWithIntegrationCurrencyDto } from '~/types/internal-api';
import { Account } from '~/app/pages/accounting/bank/accounts/types';
import internalApiClient from '~/app/shared/util/api/internalApiClient';
import { ErrorResponse } from '~/app/shared/components/modals/RequestErrorDialog';

export const getErrorResponse = (error): ErrorResponse => {
    const message = error.response.data?.message || error.message;
    return {
        message: getTranslationByEndpointErrorId(message),
        value: error.response.data?.value,
    };
};

export const areAccountsEqual = (initialAccount: Account, account: Account): boolean => {
    // We only want to compare some fields
    // this is because some props in account are either not used in the form or changed by other fields
    // f.x. formatNumber is updated when bankName changes
    const propsToCompare = [
        'bankName',
        'bankRegistrationNumber',
        'bankAccountNumber',
        'bic',
        'iban',
        'pbsNo',
        'pbsCustGrNo',
        'bankCurrency',
        'bankAgreementNumber',
        'giroAccount',
        'fiSupplierNo',
        'number',
        'note',
    ];

    for (const key of propsToCompare) {
        if (initialAccount[key] !== account[key]) {
            return false;
        }
    }

    return true;
};

export const mapBankAccount = (dataAccounts: PaymentAccountWithIntegrationCurrencyDto[]): Account[] =>
    dataAccounts.map(({ paymentAccount, integrationCurrency }) => ({
        ...paymentAccount,
        integrationCurrency,
        bankCurrency: integrationCurrency || paymentAccount.bankCurrency,
        id: `${paymentAccount.setupId}${paymentAccount.number}`,
    }));

export const addBankAccount = (account: Account) => {
    return internalApiClient.post<Account>('paymentAccounts', account).then(response => {
        const { data: newAccount } = response;
        const { setupId, number } = newAccount;
        newAccount.id = `${setupId}${number}`;

        return newAccount;
    });
};

export const CoAAlreadyUsed = (account: Account, accounts: Account[]): boolean => {
    const accountUsingCoA = accounts.find(item => item.id !== account.id && item.number === account.number);

    return !!accountUsingCoA;
};

export const formatAccountNumber = value => {
    const requiredLength = 10;
    const allowedLength = 7;
    const trimmedValue = value.trim();
    const { length } = trimmedValue;

    if (Number(trimmedValue) && length < requiredLength && length !== allowedLength) {
        return trimmedValue.padStart(requiredLength, '0');
    }

    return value;
};

export const getSelectedBank = (bankName: string, banks: BankDto[]): BankDto | undefined =>
    banks.find(item => item.name === bankName);

export const syncFields = (fieldName: string, value, form) => {
    form.change(`${fieldName}DK`, value);
    form.change(fieldName, value);
};

export const onAccountNumberBlur = (value, form) => {
    const formattedValue = formatAccountNumber(value);
    syncFields('bankAccountNumber', formattedValue, form);
    // We need to manually blur the field as final form relies on blur events to trigger validation
    form.blur('bankAccountNumberDK');
};

export const getSmartbankAccount = (number: number, smartbankAccounts: AccountModel[]) => {
    return smartbankAccounts.find(account => account.paymentAccountNumber === number);
};

export const getTranslationByEndpointErrorId = (errorId: string) => {
    switch (errorId) {
        // POST
        case 'PAYMENT_ACCOUNT_CREATE_ALREADYUSEDCOA_ERROR':
            return t`An account already exists with this Account from CoA.`;
        case 'PAYMENT_ACCOUNT_CREATE_GENERIC_ERROR':
            return t`Oops! A system error has occurred.`;

        // PUT
        case 'PAYMENT_ACCOUNT_UPDATE_WRONGCOA_ERROR':
            return t`No account to update with this Account from CoA.`;
        case 'PAYMENT_ACCOUNT_UPDATE_ALREADYUSEDCOA_ERROR':
            return t`An account already exists with this Account from CoA.`;
        case 'PAYMENT_ACCOUNT_UPDATE_GENERIC_ERROR':
            return t`Oops! A system error has occurred.`;

        // DELETE
        case 'PAYMENT_ACCOUNT_DELETE_SMARTPAY_ERROR':
            return t`The bank account is used in Smart Pay and cannot be deleted.`;
        case 'PAYMENT_ACCOUNT_DELETE_DOESNTEXIST_ERROR':
            return t`No bank account exists with these parameters.`;
        case 'PAYMENT_ACCOUNT_DELETE_JOURNALS_ERROR':
            return t`The bank account is used in a journal setup and cannot be deleted.`;
        case 'PAYMENT_ACCOUNT_DELETE_INTLEDGER_ERROR':
            return t`This bank account cannot be deleted as it has an international ledger set up and is linked to an entry.`;
        case 'PAYMENT_ACCOUNT_DELETE_GENERIC_ERROR':
            return t`Oops! A system error has occurred.`;
        default:
            return '';
    }
};

export const getHasReconciledEntries = ({ number, bankCurrency }: Account) =>
    internalApiClient.get(
        `bank-reconciliation/cash-account/${number}/bank-entries/has-reconciled-entries${
            bankCurrency ? `?currency=${bankCurrency}` : ''
        }`
    );
